import { Clear } from '@mui/icons-material';
import { IconButton, InputAdornment, Table as MuiTable, TableBody, TextField } from '@mui/material';
import { useDebouncedChangeHandler } from 'app/common/hooks/use-debounced-change-handler';
import theme from 'app/common/materialTheme';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import TableHead from './table-head.js';
import TableRow from './table-row.js';
import type { TableData, Table as TableProps } from './types.js';

export const TableContainer = styled(MuiTable)`
  width: 100%;
  font-size: 0.9rem;
  color: ${({ theme }): string => theme.palette.text.primary};
  text-align: left;
  border-spacing: 0;
`;

const FilterTextField = styled(TextField)`
  margin-bottom: ${theme.spacing(2)};
`;

const FilterNoResults = styled.div`
  margin: ${theme.spacing(2)} 0;
`;

const matchesFilter = (filterFields: string[], filterValue: string, rowData: TableData) => {
  return filterFields.some((field) => rowData[field]?.toLowerCase().includes(filterValue.toLowerCase()));
};

const getFilteredRows = (data: TableData[], filterValue: string, filterFields?: string[]) => {
  return filterFields === undefined
    ? data
    : data.filter((rowData: TableData) => matchesFilter(filterFields, filterValue, rowData));
};

const Table: React.FC<TableProps> = (props: TableProps) => {
  const {
    data,
    columns,
    onRowClick,
    tableName,
    idFieldName,
    disabledFieldName,
    updateSort,
    sort,
    disabled,
    padding,
    stickyHeader,
    filterFields,
    maxHeight,
    onFilterValueChanged,
    size
  } = props;

  const [filterText, setFilterText] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const filteredData: TableData[] = useMemo(
    () => getFilteredRows(data, filterValue, filterFields),
    [data, filterFields, filterValue]
  );

  const updateFilterValue = useCallback(
    (value: string) => {
      setFilterValue(value);
      if (onFilterValueChanged && idFieldName !== undefined) {
        const rowToId = (row: TableData) => row[idFieldName];
        const allIds = data.map(rowToId);
        const filteredIds = getFilteredRows(data, value, filterFields).map(rowToId);
        onFilterValueChanged(allIds.filter((id) => !filteredIds.includes(id)));
      }
    },
    [onFilterValueChanged, data, filterFields, idFieldName]
  );

  const debouncedSetFilterValue = useDebouncedChangeHandler(updateFilterValue, 300);

  const setFilter = useCallback(
    (value: string) => {
      setFilterText(value);
      debouncedSetFilterValue.run(value);
    },
    [debouncedSetFilterValue]
  );

  const rows = filteredData.map((rowData: TableData, index: number) => {
    return (
      <TableRow
        // biome-ignore lint/suspicious/noArrayIndexKey: intentionally using index as key
        key={index}
        index={index}
        onRowClick={onRowClick}
        columns={columns}
        data={rowData}
        tableName={tableName}
        idFieldName={idFieldName}
        disabledFieldName={disabledFieldName}
        disabled={disabled}
      />
    );
  });

  return (
    <>
      {filterFields !== undefined && (
        <FilterTextField
          label="Filter"
          variant="standard"
          style={{ width: '100%' }}
          value={filterText}
          onChange={(e) => {
            setFilter(e.target.value);
          }}
          InputProps={{
            endAdornment:
              filterValue === '' ? null : (
                <InputAdornment position="end">
                  <IconButton
                    title="Clear"
                    onClick={() => {
                      setFilter('');
                    }}
                    size="large"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
          }}
        />
      )}
      <div
        style={{
          maxHeight: maxHeight === undefined ? 'auto' : `${maxHeight}px`,
          overflowY: maxHeight === undefined ? 'visible' : 'auto',
          overflowX: 'hidden'
        }}
      >
        <TableContainer padding={padding} stickyHeader={stickyHeader === true} size={size}>
          <TableHead tableName={tableName} columns={columns} updateSort={updateSort} sort={sort} />
          <TableBody>{rows}</TableBody>
        </TableContainer>
        {filterValue !== '' && rows.length < 1 && <FilterNoResults>No rows match filter</FilterNoResults>}
      </div>
    </>
  );
};

export default Table;
